import { type IDBPDatabase, openDB } from 'idb';
import { token } from '@/infrastructure/container';
import { type ILogger } from './Logger';

export interface ILocalData {
  get<T>(store: string, key: string): Promise<T | undefined>;
  put<T>(store: string, key: string, value: T): Promise<void>;
  delete(store: string, key: string): Promise<void>;
  all<T>(store: string): Promise<T[]>;
}

export const TOKEN_ILocalData = token<ILocalData>('ILocalData');

// Deliberately still 'book-beats' after the Lenivox rename. This names the IndexedDB database
// holding every user's uploaded books and reading positions. Rename it and every existing user
// opens the app to an empty library. Do not "finish the rename" here.
const DB_NAME = 'book-beats';
const DB_VERSION = 1;
const STORES = ['book', 'progress'] as const;

export class LocalData implements ILocalData {
  private readonly dbPromise: Promise<IDBPDatabase>;

  constructor(private logger: ILogger) {
    this.dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        for (const name of STORES) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        }
      },
    });
  }

  async get<T>(store: string, key: string): Promise<T | undefined> {
    this.logger.debug('LocalData', `get ${store}/${key}`);
    return (await this.dbPromise).get(store, key) as Promise<T | undefined>;
  }

  async put<T>(store: string, key: string, value: T): Promise<void> {
    this.logger.debug('LocalData', `put ${store}/${key}`);
    await (await this.dbPromise).put(store, value as never, key);
  }

  async delete(store: string, key: string): Promise<void> {
    this.logger.debug('LocalData', `delete ${store}/${key}`);
    await (await this.dbPromise).delete(store, key);
  }

  async all<T>(store: string): Promise<T[]> {
    this.logger.debug('LocalData', `all ${store}`);
    return (await this.dbPromise).getAll(store) as Promise<T[]>;
  }
}

export class InMemoryLocalData implements ILocalData {
  private data = new Map<string, Map<string, unknown>>();

  private bucket(store: string): Map<string, unknown> {
    let m = this.data.get(store);
    if (!m) {
      m = new Map();
      this.data.set(store, m);
    }
    return m;
  }

  async get<T>(store: string, key: string): Promise<T | undefined> {
    return this.bucket(store).get(key) as T | undefined;
  }

  async put<T>(store: string, key: string, value: T): Promise<void> {
    this.bucket(store).set(key, value);
  }

  async delete(store: string, key: string): Promise<void> {
    this.bucket(store).delete(key);
  }

  async all<T>(store: string): Promise<T[]> {
    return [...this.bucket(store).values()] as T[];
  }
}
